import BaseStore from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';

import type {
  CustomRole,
  CustomRoleLevel,
  RoleId,
  RoleNotification,
} from 'bundles/teach-partner-2018/custom-roles/types/CustomRole';

type CustomRoleStore$DehydratedState = {
  roles: CustomRole[];
  notification: RoleNotification | null;
};

class CustomRoleStore extends BaseStore {
  declare roles: CustomRole[];

  declare notification: RoleNotification | null;

  declare error: string;

  declare defaultRoles: CustomRole[];

  declare selectedRoleLevel: CustomRoleLevel;

  static storeName = 'CustomRoleStore';

  static handlers = {
    RECEIVED_ROLE_LIST: 'onReceivedRoleList',
    RECEIVED_DEFAULT_ROLE_LIST: 'onReceivedDefaultRoleList',
    SINGLE_ROLE_ADDITION: 'onSingleRoleAddition',
    SINGLE_ROLE_DELETION: 'onSingleRoleDeletion',
    SELECTED_ROLE_LEVEL_CHANGED: 'onSelectedRoleLevelChanged',
    RECEIVED_ROLE_UPDATE: 'onRoleUpdate',
    RECEIVED_ERROR: 'errorCallback',
    SET_NOTIFICATION: 'setNotification',
    CLEAR_NOTIFICATION: 'clearNotification',
  };

  constructor(dispatcher: $TSFixMe) {
    super(dispatcher);
    this.selectedRoleLevel = 'COURSE';
    this.roles = [];
    this.error = '';
    this.notification = null;
    this.defaultRoles = [];
  }

  onReceivedRoleList(roleListPayload: CustomRole[]) {
    // TODO: remove this filter when support for other levels is added
    this.roles = [...roleListPayload].filter((role) => role.level === 'COURSE');
    this.emitChange();
  }

  onReceivedDefaultRoleList(roleListPayload: CustomRole[]) {
    this.defaultRoles = [...roleListPayload];
    this.emitChange();
  }

  onSingleRoleAddition(newRole: CustomRole) {
    this.roles.unshift(newRole);
    this.emitChange();
  }

  onSingleRoleDeletion(id: RoleId) {
    this.roles = this.roles.filter((role) => role.id !== id);
    this.emitChange();
  }

  onSelectedRoleLevelChanged(level: CustomRoleLevel) {
    this.selectedRoleLevel = level;
    this.emitChange();
  }

  onRoleUpdate({ id, updatedRolePayload }: { id: RoleId; updatedRolePayload: CustomRole }) {
    if (updatedRolePayload && updatedRolePayload.id === id) {
      this.roles = this.roles.filter((role) => role.id !== id);
      this.roles.unshift(updatedRolePayload);
      this.emitChange();
    }
  }

  errorCallback() {
    this.notification = { type: 'error', message: 'Something went wrong. Please try again.' };
    this.emitChange();
  }

  setNotification({ type, message }: RoleNotification) {
    this.notification = { type, message };
    this.emitChange();
  }

  clearNotification() {
    this.notification = null;
    this.emitChange();
  }

  getRoleList() {
    return this.roles;
  }

  getCustomRoleNamesList() {
    return this.roles.reduce((acc: string[], role: CustomRole): string[] => {
      return acc.concat(role.name);
    }, []);
  }

  getNotification() {
    return this.notification;
  }

  getDefaultCustomRole() {
    return this.defaultRoles.find((role) => role.level === this.selectedRoleLevel);
  }

  getDefaultCustomRoleByLevel(level: CustomRoleLevel) {
    return this.defaultRoles.find((role) => role.level === level);
  }

  dehydrate(): CustomRoleStore$DehydratedState {
    return {
      roles: this.roles,
      notification: this.notification,
    };
  }

  rehydrate(state: CustomRoleStore$DehydratedState) {
    this.roles = [...state.roles];
    this.notification = state.notification;
  }
}

export default CustomRoleStore;
